import React, { useState } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const KYC_CHECKLIST = [
  {
    id: "cert_of_incorporation",
    label: "Certificate of Incorporation",
    description: "Official document confirming legal existence of the entity",
    required: true,
    category: "Corporate Documents",
  },
  {
    id: "memorandum",
    label: "Memorandum & Articles of Association",
    description: "Constitutional documents outlining company structure and rules",
    required: true,
    category: "Corporate Documents",
  },
  {
    id: "register_of_directors",
    label: "Register of Directors",
    description: "Current list of all directors with identification details",
    required: true,
    category: "Corporate Documents",
  },
  {
    id: "register_of_shareholders",
    label: "Register of Shareholders / Members",
    description: "Current shareholder register showing beneficial ownership",
    required: true,
    category: "Beneficial Ownership",
  },
  {
    id: "ubo_declaration",
    label: "Ultimate Beneficial Owner (UBO) Declaration",
    description: "Declaration of all individuals owning 25%+ of the entity",
    required: true,
    category: "Beneficial Ownership",
  },
  {
    id: "passport_directors",
    label: "Passport / ID for All Directors",
    description: "Government-issued photo identification for each director",
    required: true,
    category: "Identity Verification",
  },
  {
    id: "proof_of_address",
    label: "Proof of Registered Address",
    description: "Utility bill or bank statement dated within 3 months",
    required: true,
    category: "Identity Verification",
  },
  {
    id: "aml_policy",
    label: "AML / Compliance Policy",
    description: "Your organisation's anti-money laundering policy document",
    required: false,
    category: "AML & Compliance",
  },
  {
    id: "source_of_funds",
    label: "Source of Funds Declaration",
    description: "Documentation evidencing the origin of investment capital",
    required: true,
    category: "AML & Compliance",
  },
  {
    id: "sanctions_check",
    label: "Sanctions Screening Confirmation",
    description: "Confirmation that entity and principals are not on sanctions lists",
    required: true,
    category: "AML & Compliance",
  },
];

const CATEGORIES = [...new Set(KYC_CHECKLIST.map((i) => i.category))];

const STATUS_CONFIG = {
  not_started: { label: "Not Started", color: "text-muted-foreground", bg: "bg-muted", icon: "Circle" },
  uploaded: { label: "Uploaded", color: "text-warning", bg: "bg-amber-50", icon: "Upload" },
  verified: { label: "Verified", color: "text-success", bg: "bg-green-50", icon: "CheckCircle2" },
};

export default function StepKYCAML({ data, onChange, errors }) {
  const [uploadingId, setUploadingId] = useState(null);

  const getItemStatus = (id) => data?.kycStatuses?.[id] || "not_started";

  const handleSimulateUpload = (id) => {
    setUploadingId(id);
    setTimeout(() => {
      onChange({
        ...data,
        kycStatuses: { ...data?.kycStatuses, [id]: "uploaded" },
      });
      setUploadingId(null);
    }, 1200);
  };

  const handleAttestation = (field) => (e) => {
    onChange({ ...data, [field]: e?.target?.checked });
  };

  const completedCount = KYC_CHECKLIST?.filter(
    (item) => getItemStatus(item?.id) !== "not_started"
  )?.length;

  const progressPct = Math.round((completedCount / KYC_CHECKLIST?.length) * 100);

  return (
    <div className="space-y-6">
      {/* Progress summary */}
      <div className="p-4 rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between mb-2">
          <p className="font-caption font-semibold text-sm text-foreground">Document Submission Progress</p>
          <span className="font-data text-sm text-primary font-semibold">{completedCount}/{KYC_CHECKLIST?.length}</span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%`, background: "var(--color-primary)" }}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-1">{progressPct}% complete</p>
      </div>
      {/* Checklist by category */}
      {CATEGORIES?.map((category) => (
        <div key={category}>
          <h4 className="font-caption font-semibold text-sm text-foreground mb-3 flex items-center gap-2">
            <span className="w-1.5 h-4 rounded-full bg-primary inline-block" />
            {category}
          </h4>
          <div className="space-y-2">
            {KYC_CHECKLIST?.filter((item) => item?.category === category)?.map((item) => {
              const status = getItemStatus(item?.id);
              const statusCfg = STATUS_CONFIG?.[status];
              const isUploading = uploadingId === item?.id;

              return (
                <div
                  key={item?.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border transition-all duration-200 ${statusCfg?.bg} ${
                    status === "verified" ? "border-green-200" : status === "uploaded" ? "border-amber-200" : "border-border"
                  }`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <Icon
                      name={statusCfg?.icon}
                      size={16}
                      color={
                        status === "verified"
                          ? "var(--color-success)"
                          : status === "uploaded" ?"var(--color-warning)" :"var(--color-muted-foreground)"
                      }
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-caption font-medium text-sm text-foreground">{item?.label}</p>
                      {item?.required && (
                        <span className="text-xs text-error font-caption">Required</span>
                      )}
                      <span className={`text-xs font-caption ${statusCfg?.color}`}>{statusCfg?.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{item?.description}</p>
                  </div>
                  {status === "not_started" && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Upload"
                      iconPosition="left"
                      iconSize={14}
                      loading={isUploading}
                      onClick={() => handleSimulateUpload(item?.id)}
                      className="flex-shrink-0"
                    >
                      Upload
                    </Button>
                  )}
                  {status === "uploaded" && (
                    <span className="flex-shrink-0 flex items-center gap-1 text-xs text-warning font-caption">
                      <Icon name="Clock" size={12} />
                      Under review
                    </span>
                  )}
                  {status === "verified" && (
                    <span className="flex-shrink-0 flex items-center gap-1 text-xs text-success font-caption">
                      <Icon name="Check" size={12} />
                      Verified
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {/* Attestations */}
      <div className="pt-4 border-t border-border space-y-3">
        <h4 className="font-caption font-semibold text-sm text-foreground">Compliance Attestations</h4>

        {[
          {
            field: "attestPep",
            label: "I confirm that no director, officer, or UBO of the entity is a Politically Exposed Person (PEP) or is subject to sanctions.",
          },
          {
            field: "attestAml",
            label: "I confirm that the entity has adequate AML/CTF policies and procedures in place and complies with applicable regulations.",
          },
          {
            field: "attestAccuracy",
            label: "I confirm that all information and documents provided are accurate, complete, and up to date to the best of my knowledge.",
          },
        ]?.map(({ field, label }) => (
          <label key={field} className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={data?.[field] || false}
              onChange={handleAttestation(field)}
              className="mt-0.5 w-4 h-4 rounded border-border text-primary focus:ring-ring flex-shrink-0"
            />
            <span className="text-sm font-caption text-foreground group-hover:text-primary transition-colors">
              {label}
            </span>
          </label>
        ))}
        {errors?.attestations && (
          <p className="text-error text-xs">{errors?.attestations}</p>
        )}
      </div>
    </div>
  );
}